import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import Select from 'react-select';
import '../users/userform.css';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../services/AxiosInstance";
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { isBranchAdmin, getCurrentUserRole } from '../../utils/roleAccess';
import { FIELD_LIMITS } from '../../utils/fieldLimits';

const EditDepartment = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    branchId: "",
    departmentName: ""
  });
  const [departmentData, setDepartmentData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isBranchDisabled, setIsBranchDisabled] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  // Helper function to safely navigate back, avoiding login page
  const navigateBackSafely = () => {
    navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.branchId || formData.branchId === '') newErrors.branchId = 'Branch is required';
    if (!formData.departmentName.trim()) newErrors.departmentName = 'Department name is required';
    else if (formData.departmentName.length > FIELD_LIMITS.DEPARTMENT_NAME) newErrors.departmentName = `Department name cannot exceed ${FIELD_LIMITS.DEPARTMENT_NAME} characters`;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    setLoading(true);
    setIsSubmitting(true);

    const userData = JSON.parse(localStorage.getItem("user"));
    
    const payload = {
      branch_id: formData.branchId,
      name: formData.departmentName,
      corporate_id: userData.corporateId,
      department_id: id
    };
    
    try {
      const response = await axiosInstance.put(`/settings/updateDepartment`, JSON.stringify(payload));

      if (!response) {
        throw new Error('Failed to update the department');
      }

      const result = response;
      console.log('Department updated successfully:', result);
      notifyTopRight('✅ Department updated successfully!');
      
      setTimeout(() => {
        setIsSubmitting(false);
        navigateBackSafely();
      }, 2000);

    } catch (error) {
      console.error('Update error:', error);
      if (error.status === 409) {
        notifyError('Department already exists with this branch');
      } else {
        notifyError(error.response?.data?.error || 'Failed to update department');
      }
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        const corporateId = userData?.corporateId || userData?.id;
        const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
        setBranches(res.data.data || res.data || []);
      } catch (err) {
        console.error("Failed to fetch branches:", err);
        notifyError('Failed to load branches');
      }
    };

    const initializeRoleBasedFields = async () => {
      try {
        const role = getCurrentUserRole();
        
        // If user is branch_admin, disable branch field
        if (isBranchAdmin(role)) {
          setIsBranchDisabled(true);
        }
      } catch (err) {
        console.error('Error initializing role-based fields:', err);
      }
    };

    fetchBranches();
    initializeRoleBasedFields();
  }, []);

  useEffect(() => {
    const fetchDepartmentData = async () => {
      try {
        // First try to get data from location state (passed from list)
        if (location.state?.department) {
          const department = location.state.department;
          setDepartmentData(department);
          setFormData({
            branchId: department.branch_id || "",
            departmentName: department.name || ""
          });
          setDataLoaded(true);
        } else {
          // If no state data, fetch from API
          const res = await axiosInstance.get(`/settings/departments/${id}`);
          const department = res.data;
          setDepartmentData(department);
          setFormData({
            branchId: department.branch_id || "",
            departmentName: department.name || ""
          });
          setDataLoaded(true);
        }
      } catch (err) {
        console.error("Failed to fetch department data:", err);
        notifyError('Failed to load department data');
      }
    };

    if (id) {
      fetchDepartmentData();
    }
  }, [id, location.state]);

  const notifyTopRight = (data) => {
    toast.success(`✔️ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  return (
    <div>
      <button
        className="back-button"
        onClick={() => {
          if (window.history.length > 2) {
            navigate(-1);
          } else {
            navigate('/settings/departmentlist');
          }
        }}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <div className="row" style={{ marginBottom: '0px !important' }}>
        <ToastContainer />
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Edit Department</h4>
            </div>
            <div className="card-body">
              {!dataLoaded ? (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading department data...</p>
                </div>
              ) : (
                <section>
                  <div className="row">
                    <div className="col-lg-6 mb-2">
                      <div className="form-group mb-3">
                        <label className="user-form-label mb-1"><strong>Branch*</strong></label>
                        <div className="user-form-select-container">
                          <Select
                            classNamePrefix="user-form-select"
                            placeholder="Select Branch"
                            isClearable
                            isDisabled={isBranchDisabled}
                            value={branches.find((branch) => branch.id === formData.branchId) 
                              ? { value: formData.branchId, label: branches.find(b => b.id === formData.branchId)?.name }
                              : null
                            }
                            onChange={(selectedOption) => {
                              setFormData((prevData) => ({
                                ...prevData,
                                branchId: selectedOption ? selectedOption.value : ''
                              }));
                            }}
                            options={branches.map((branch) => ({
                              value: branch.id,
                              label: branch.name
                            }))}
                          />
                        </div>
                        {errors.branchId && <div className="text-danger">{errors.branchId}</div>}
                      </div>
                    </div>

                    <div className="col-lg-6 mb-2">
                      <div className="form-group mb-3">
                        <label className="text-label">Department Name*</label>
                        <input
                          type="text"
                          name="departmentName"
                          className="form-control"
                          placeholder="Enter department name"
                          maxLength={FIELD_LIMITS.DEPARTMENT_NAME}
                          value={formData.departmentName}
                          onChange={handleChange}
                        />
                        {errors.departmentName && <div className="text-danger">{errors.departmentName}</div>}
                      </div>
                    </div>

                    <div className="text-end toolbar toolbar-bottom p-2">
                      <button 
                        type="button"
                        className="btn btn-cancel me-2" 
                        onClick={navigateBackSafely}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button 
                        className="btn user-form-btn sw-btn-next" 
                        onClick={handleSubmit} 
                        disabled={isSubmitting} 
                        style={{ maxWidth: '100px' }}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : null}
                        Submit
                      </button>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
        <InternalFooter />
      </div>
    </div>
  );
};

export default EditDepartment;
